import { findListItemPos } from "@ext/markdown/elements/list/edit/logic/keymaps/findListItemPos";
import type { Editor } from "@tiptap/core";
import { isAtStartOfNode, isNodeActive, isNodeSelection } from "@tiptap/core";
import type { Node, ResolvedPos } from "@tiptap/pm/model";
import type { EditorState, Transaction } from "@tiptap/pm/state";
import { canJoin, liftTarget } from "@tiptap/pm/transform";

export const handleBackspace = (editor: Editor, name: string, parentListTypes: string[]) => {
	// this is required to still handle the undo handling
	if (editor.commands.undoInputRule()) {
		return true;
	}

	const isFocusedAtBlock = isNodeSelection(editor.state.selection);

	// if the selection is not collapsed
	// we can rely on the default backspace behavior
	if (editor.state.selection.from !== editor.state.selection.to && !isFocusedAtBlock) {
		return false;
	}

	if (isCursorBlockAfterList(editor.state, parentListTypes)) {
		return joinCursorBlockToLastListLine(editor, name);
	}

	// if the cursor is not inside the current node type
	// do nothing and proceed
	if (!isNodeActive(editor.state, name)) {
		return false;
	}

	// if the cursor at first block node
	// delete this block node
	const { $from, to } = editor.state.selection;
	const isBlockNodeAtStartOfNode = isFocusedAtBlock && $from.pos + 1 === to && $from.parentOffset === 0;
	if (isBlockNodeAtStartOfNode) {
		return editor.chain().deleteRange({ from: $from.pos, to }).run();
	}

	const listItemPos = findListItemPos(name, editor.state);

	if (!listItemPos) {
		return false;
	}

	// if the cursor is not at the start of a node and not start of the list item
	// do nothing and proceed
	if (!isAtStartOfNode(editor.state) || listItemPos.$pos.parentOffset > 0) {
		return false;
	}

	return removeListItemMarker(editor, name, parentListTypes, listItemPos.$pos);
};

const isCursorBlockAfterList = (state: EditorState, listTypes: string[]) => {
	const { selection } = state;
	const { $from } = selection;
	if (!selection.empty || !$from.parent.isTextblock || $from.parentOffset > 0) return false;

	const index = $from.index(-1);
	return index > 0 && listTypes.includes($from.node(-1).child(index - 1).type.name);
};

const joinCursorBlockToLastListLine = (editor: Editor, name: string) => {
	const { $anchor } = editor.state.selection;

	const ListPos = editor.state.doc.resolve($anchor.before() - 1);

	const listDescendants: Array<{ node: Node; pos: number }> = [];

	ListPos.node().descendants((node, pos) => {
		if (node.type.name === name) {
			listDescendants.push({ node, pos });
		}
	});

	const lastItem = listDescendants.at(-1);

	if (!lastItem) {
		return false;
	}

	const LastItemPos = editor.state.doc.resolve(ListPos.start() + lastItem.pos + 1);
	const lastChild = lastItem.node.lastChild;
	const isBlockWithoutContent = lastChild?.isBlock && !lastChild?.type.spec.content;
	const insertPos = isBlockWithoutContent ? LastItemPos.end() - 1 : LastItemPos.end();

	return editor
		.chain()
		.cut({ from: $anchor.start() - 1, to: $anchor.end() + 1 }, insertPos)
		.joinForward()
		.run();
};

const removeListItemMarker = (editor: Editor, name: string, listTypes: string[], itemContentStart: ResolvedPos) => {
	const item = itemContentStart.parent;
	const itemPos = itemContentStart.before();
	const itemInList = editor.state.doc.resolve(itemPos);

	if (itemInList.nodeBefore?.type.name === name) {
		return editor
			.chain()
			.command(({ tr }) => joinToPreviousItem(tr, itemPos))
			.run();
	}

	if (itemInList.node(-1).type.name === name) {
		return editor
			.chain()
			.command(({ tr }) => liftIntoParentItem(tr, itemContentStart, listTypes))
			.run();
	}

	return editor
		.chain()
		.liftListItem(name)
		.command(({ tr }) => joinListsAfterLiftedBlocks(tr, item.childCount, listTypes))
		.run();
};

const joinToPreviousItem = (tr: Transaction, itemPos: number) => {
	if (!canJoin(tr.doc, itemPos)) return false;
	tr.join(itemPos);
	return true;
};

const liftIntoParentItem = (tr: Transaction, itemContentStart: ResolvedPos, listTypes: string[]) => {
	const range = itemContentStart.blockRange(tr.doc.resolve(itemContentStart.end()));
	if (!range) return false;
	const target = liftTarget(range);
	if (target === null) return false;

	tr.lift(range, target);
	return joinListsAfterLiftedBlocks(tr, range.endIndex - range.startIndex, listTypes);
};

// Lifted nested items and the rest of the list they came from land side by side as two lists of the same level.
const joinListsAfterLiftedBlocks = (tr: Transaction, liftedCount: number, listTypes: string[]) => {
	const { $from } = tr.selection;
	const container = $from.node(-1);
	const lastIndex = $from.index(-1) + liftedCount - 1;
	const last = container.maybeChild(lastIndex);
	const next = container.maybeChild(lastIndex + 1);
	if (!last || next?.type !== last.type || !listTypes.includes(last.type.name)) return true;

	const nextPos = $from.posAtIndex(lastIndex + 1, -1);
	if (canJoin(tr.doc, nextPos)) tr.join(nextPos);
	return true;
};
